import React, { useMemo, useState } from 'react';
import { Users, Check, X, Loader2 } from 'lucide-react';
import Avatar from './Avatar';
import { respondCohostRequest } from '../api/lives';
import { useToast } from '../hooks/use-toast';
import liveDiag from '../lib/liveDiag'; // [LIVE-DIAG] temporary

/**
 * CohostRequestsPanel — host-side panel showing "Partecipa alla live"
 * requests sent by viewers. Renders the list of `guests` with
 * status === 'requested' and provides accept/decline buttons.
 */
const CohostRequestsPanel = ({ live, onChanged }) => {
  const { toast } = useToast();
  const [busyId, setBusyId] = useState(null);

  const requests = useMemo(
    () => (live?.guests || []).filter((g) => g.status === 'requested'),
    [live],
  );

  const decide = async (userId, accept) => {
    setBusyId(userId);
    try {
      await respondCohostRequest(live.id, userId, accept);
      liveDiag(accept ? 'participation_request_approved' : 'participation_request_declined', { live_id: live.id, guest_id: userId }); // [LIVE-DIAG]
      toast({ title: accept ? 'Partecipazione approvata' : 'Richiesta rifiutata' });
      onChanged?.();
    } catch (_e) {
      toast({ title: 'Impossibile rispondere', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="cohost-requests-panel">
      <header className="flex items-center justify-between border-b border-border px-4 py-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Users className="h-4 w-4" /> Richieste di partecipazione
        </h3>
        {requests.length > 0 && (
          <span
            className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold gold-text"
            data-testid="cohost-requests-badge"
          >
            {requests.length}
          </span>
        )}
      </header>
      {requests.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-muted-foreground">
          Nessuna richiesta di partecipazione al momento.
        </p>
      ) : (
        <ul className="max-h-72 overflow-y-auto divide-y divide-border/60">
          {requests.map((r) => (
            <li
              key={r.user_id}
              className="px-4 py-2.5"
              data-testid={`cohost-request-row-${r.user_id}`}
            >
              <div className="flex items-start gap-2.5">
                <Avatar src={r.avatar_url} name={r.username} size={36} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">@{r.username}</p>
                  {r.message && (
                    <p className="mt-0.5 line-clamp-2 text-[12px] text-muted-foreground">
                      “{r.message}”
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    onClick={() => decide(r.user_id, true)}
                    disabled={busyId === r.user_id}
                    title="Approva la partecipazione"
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-50"
                    data-testid={`cohost-accept-${r.user_id}`}
                  >
                    {busyId === r.user_id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Check className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => decide(r.user_id, false)}
                    disabled={busyId === r.user_id}
                    title="Rifiuta"
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-red-500/15 text-red-300 hover:bg-red-500/30 disabled:opacity-50"
                    data-testid={`cohost-decline-${r.user_id}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CohostRequestsPanel;
